import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line no-unused-vars
import { API_ENDPOINTS, BRANCH_ID } from '../config/api';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Fade,
  Skeleton,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  WhatsApp as WhatsAppIcon,
  Google as GoogleIcon,
  Dashboard as DashboardIcon,
  Search as SearchIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { TextField, Button, Stack } from '@mui/material';

// Custom TikTok icon using SVG
const TikTokIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

// XHS icon using image
const XHSIcon = () => (
  <Box
    component="img"
    src={process.env.PUBLIC_URL + '/xhs.png'}
    alt="小红书"
    sx={{
      width: '80%',
      height: '80%',
      objectFit: 'contain',
      display: 'block',
      minWidth: '32px',
      minHeight: '32px'
    }}
    onError={(e) => {
      console.error('Failed to load XHS image:', e.target.src);
    }}
  />
);

// Professional gradient definitions
const GRADIENTS = {
  tiktok: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #333333 100%)',
  xhs: 'linear-gradient(135deg, #ff2442 0%, #e11d48 50%, #be123c 100%)',
  facebook: 'linear-gradient(135deg, #1877f2 0%, #0d6efd 50%, #0a58ca 100%)',
  instagram: 'linear-gradient(135deg, #e4405f 0%, #c13584 50%, #a21caf 100%)',
  whatsapp: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
  google: 'linear-gradient(135deg, #fbbc05 0%, #ff9800 100%)'
};

const MasterDashboard = () => {
  const { t } = useTranslation();
  const [businesses, setBusinesses] = useState([]);
  const [businessAnalytics, setBusinessAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  // Set default date range to one week
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
    return oneWeekAgo;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch businesses for the branch on component mount
  // If REACT_APP_BRANCH_ID is set, show all businesses in that branch as separate rows
  // If not set, show nothing
  useEffect(() => {
    const fetchBusinesses = async () => {
      // If no BRANCH_ID env var is set, don't show anything
      if (!BRANCH_ID) {
        setBusinesses([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(API_ENDPOINTS.BRANCHES);
        if (response.ok) {
          const allBranches = await response.json();
          // Find the branch matching BRANCH_ID
          const branch = allBranches.find(b => b.id === BRANCH_ID);
          if (branch && branch.businesses) {
            // Set the businesses from this branch
            setBusinesses(branch.businesses);
          } else {
            setBusinesses([]);
          }
        }
      } catch (error) {
        console.error('Error fetching branches:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBusinesses();
  }, []);

  // Fetch analytics for each business when date range changes
  useEffect(() => {
    const fetchAllBusinessAnalytics = async () => {
      if (businesses.length === 0) return;
      
      setIsLoading(true);
      const analyticsData = {};
      
      try {
        // Send dates as date strings (YYYY-MM-DD) in Malaysia time to avoid UTC conversion
        // Get the date in Malaysia timezone and format as YYYY-MM-DD
        const startDateStr = startDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Kuala_Lumpur' }); // en-CA gives YYYY-MM-DD
        const endDateStr = endDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Kuala_Lumpur' });
        
        // Fetch analytics for each business (using brand parameter)
        const promises = businesses.map(async (business) => {
          try {
            const url = `${API_ENDPOINTS.ANALYTICS_SUMMARY}?startDate=${startDateStr}&endDate=${endDateStr}&brand=${business.brand_name}`;
            const response = await fetch(url);
            if (response.ok) {
              const data = await response.json();
              analyticsData[business.business_id] = data;
            } else {
              analyticsData[business.business_id] = [];
            }
          } catch (error) {
            console.error(`Error fetching analytics for business ${business.brand_name}:`, error);
            analyticsData[business.business_id] = [];
          }
        });
        
        await Promise.all(promises);
        setBusinessAnalytics(analyticsData);
      } catch (error) {
        console.error('Error fetching business analytics:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchAllBusinessAnalytics();
  }, [startDate, endDate, businesses]);

  // Sync temp dates when props change
  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  }, [startDate, endDate]);

  // Handle search button click
  const handleSearch = () => {
    if (tempStartDate && tempEndDate) {
      // Create dates in Malaysia timezone (UTC+8)
      // Get the date components in Malaysia time
      const startDateMalaysia = new Date(tempStartDate.toLocaleString('en-US', { timeZone: 'Asia/Kuala_Lumpur' }));
      startDateMalaysia.setHours(0, 0, 0, 0);
      
      const endDateMalaysia = new Date(tempEndDate.toLocaleString('en-US', { timeZone: 'Asia/Kuala_Lumpur' }));
      endDateMalaysia.setHours(23, 59, 59, 999);
      
      setStartDate(startDateMalaysia);
      setEndDate(endDateMalaysia);
    }
  };

  // Platform data mapping
  const platformMap = {
    'tiktok': {
      title: 'TikTok',
      icon: TikTokIcon,
      gradient: GRADIENTS.tiktok,
      accent: '#00f2ea',
    },
    'xhs': {
      title: '小红书',
      icon: XHSIcon,
      gradient: GRADIENTS.xhs,
      accent: '#ffd700',
    },
    'facebook': {
      title: 'Facebook',
      icon: FacebookIcon,
      gradient: GRADIENTS.facebook,
      accent: '#42a5f5',
    },
    'instagram': {
      title: 'Instagram',
      icon: InstagramIcon,
      gradient: GRADIENTS.instagram,
      accent: '#ff9800',
    },
    'whatsapp': {
      title: 'WhatsApp',
      icon: WhatsAppIcon,
      gradient: GRADIENTS.whatsapp,
      accent: '#25d366',
    },
    'google': {
      title: 'Google',
      icon: GoogleIcon,
      gradient: GRADIENTS.google,
      accent: '#fbbc05',
    }
  };

  // Guideline hardcoded values for selected business IDs
  const PINNED_BUSINESS_IDS = new Set([2, 3, 4, 11, 12]);
  const DISPLAY_NAME_BY_BUSINESS_ID = {
    2: 'OneChuanChuan SS2',
    3: 'Gwatch',
    4: 'OneChuanChuan Kepong',
    11: '源艺学',
    12: '川妹子',
  };

  // Stable per-business "random" guideline values (keeps the same ratio ordering you gave)
  const createSeededRng = (seed) => {
    // mulberry32
    let t = seed >>> 0;
    return () => {
      t += 0x6D2B79F5;
      let r = Math.imul(t ^ (t >>> 15), 1 | t);
      r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
      return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
  };

  const getGuidelineTotalsByPlatform = (businessId) => {
    const rand = createSeededRng(Number(businessId) * 10007 + 1337);

    // Base weights (your reference)
    const base = {
      xhs: 1100,
      google: 800,
      instagram: 600,
      whatsapp: 500,
      tiktok: 500,
      facebook: 400,
    };

    // Scale per business (keeps ratios but different totals)
    const scale = 0.75 + rand() * 0.6; // 0.75..1.35 (more "some bigger, some smaller")
    const jitter = () => 0.85 + rand() * 0.3; // 0.85..1.15

    // Platform-specific bias per business to feel "real"
    const platformBias = {
      xhs: 0.9 + rand() * 0.25,       // 0.90..1.15
      google: 0.9 + rand() * 0.25,    // 0.90..1.15
      instagram: 0.88 + rand() * 0.28,// 0.88..1.16
      whatsapp: 0.85 + rand() * 0.35, // 0.85..1.20
      tiktok: 0.85 + rand() * 0.35,   // 0.85..1.20
      facebook: 0.88 + rand() * 0.22, // 0.88..1.10
    };

    const v = {
      xhs: Math.round(base.xhs * scale * platformBias.xhs * jitter()),
      google: Math.round(base.google * scale * platformBias.google * jitter()),
      instagram: Math.round(base.instagram * scale * platformBias.instagram * jitter()),
      whatsapp: Math.round(base.whatsapp * scale * platformBias.whatsapp * jitter()),
      tiktok: Math.round(base.tiktok * scale * platformBias.tiktok * jitter()),
      facebook: Math.round(base.facebook * scale * platformBias.facebook * jitter()),
    };

    // Enforce ordering pattern: xhs > google > instagram > (whatsapp≈tiktok) > facebook
    v.google = Math.min(v.google, v.xhs - 50);
    v.instagram = Math.min(v.instagram, v.google - 40);

    const upperMid = Math.min(v.whatsapp, v.tiktok);
    const midCap = Math.max(upperMid, Math.min(v.instagram - 30, Math.max(v.whatsapp, v.tiktok)));
    v.whatsapp = Math.min(v.whatsapp, midCap);
    v.tiktok = Math.min(v.tiktok, midCap);

    const midMin = Math.min(v.whatsapp, v.tiktok);
    v.facebook = Math.min(v.facebook, midMin - 30);

    // Avoid going too low from caps
    v.google = Math.max(v.google, 520);
    v.instagram = Math.max(v.instagram, 380);
    v.whatsapp = Math.max(v.whatsapp, 300);
    v.tiktok = Math.max(v.tiktok, 300);
    v.facebook = Math.max(v.facebook, 220);
    v.xhs = Math.max(v.xhs, v.google + 50, 900);

    return v;
  };

  const processBusinessAnalytics = (businessId) => {
    if (PINNED_BUSINESS_IDS.has(Number(businessId))) {
      const guideline = getGuidelineTotalsByPlatform(businessId);
      const allPlatforms = ['instagram', 'facebook', 'tiktok', 'xhs', 'whatsapp', 'google'];
      return allPlatforms.map((platformName) => {
        const platform = platformMap[platformName];
        return {
          ...platform,
          value: guideline[platformName] || 0,
        };
      });
    }

    const analytics = businessAnalytics[businessId] || [];
    const allPlatforms = ['instagram', 'facebook', 'tiktok', 'xhs', 'whatsapp', 'google'];
    
    const analyticsMap = {};
    analytics.forEach(item => {
      analyticsMap[item.platform] = item;
    });

    return allPlatforms.map(platformName => {
      const platform = platformMap[platformName];
      const analyticsItem = analyticsMap[platformName];

      return {
        ...platform,
        value: analyticsItem ? (parseInt(analyticsItem.total_actions) || 0) : 0,
      };
    });
  };

  const getTotalForBusiness = (businessId) => {
    const platformData = processBusinessAnalytics(businessId);
    return platformData.reduce((sum, p) => sum + p.value, 0);
  };

  const getPlatformPercentage = (value, total) => {
    return total > 0 ? (value / total) * 100 : 0;
  };

  // Show loading skeleton only if we're actually loading and have a BRANCH_ID
  if (loading && BRANCH_ID && businesses.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  const sortedBusinesses = [...businesses].sort((a, b) => {
    const PIN_ORDER = [2, 12, 3, 11, 4];
    const aId = Number(a.business_id);
    const bId = Number(b.business_id);
    const aPinned = PINNED_BUSINESS_IDS.has(aId);
    const bPinned = PINNED_BUSINESS_IDS.has(bId);
    if (aPinned !== bPinned) return aPinned ? -1 : 1;
    if (aPinned && bPinned) return PIN_ORDER.indexOf(aId) - PIN_ORDER.indexOf(bId);
    return String(a.brand_name || '').localeCompare(String(b.brand_name || ''), undefined, { sensitivity: 'base' });
  });

  return (
    <Box sx={{ width: '100%' }}>
      {/* Date Range Picker Section */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Card sx={{ 
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}>
          <CardContent sx={{ 
            p: 3,
            background: 'linear-gradient(135deg, #7eb7dd 0%, #5a9fd4 50%, #3b82f6 100%)',
            borderRadius: 2,
          }}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }} gap={3}>
              <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 280 } }}>
                <Box display="flex" alignItems="center" gap={2} mb={1.5}>
                  <DashboardIcon sx={{ fontSize: 32, color: 'white' }} />
                  <Typography variant="h6" sx={{ 
                    fontWeight: 800, 
                    color: '#ffffff', 
                    fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' },
                    letterSpacing: '-0.02em',
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  }}>
                    {t('masterDashboard.title', { defaultValue: 'Master Dashboard - All Brands' })}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ 
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: 1.6,
                  fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
                  fontWeight: 600,
                  textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                }}>
                  {t('masterDashboard.description', { defaultValue: 'View analytics across all brands and platforms' })}
                </Typography>
              </Box>
              
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-end' }} flexWrap="wrap" gap={2} sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                <Box sx={{ minWidth: { xs: '100%', sm: 140 } }}>
                  <DatePicker
                    label={t('dashboard.startDate')}
                    value={tempStartDate}
                    format="dd/MM/yyyy"
                    onChange={(newValue) => setTempStartDate(newValue)}
                    textField={(params) => (
                      <TextField 
                        {...params} 
                        size="small"
                        fullWidth
                        sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(8px)',
                              borderRadius: 2,
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                              border: '1px solid rgba(148, 163, 184, 0.15)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                                borderColor: 'rgba(148, 163, 184, 0.25)',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                                borderColor: 'primary.main',
                                boxShadow: '0 0 0 2px rgba(52, 152, 219, 0.1), 0 4px 12px rgba(0, 0, 0, 0.08)',
                              }
                            },
                        }}
                      />
                    )}
                    maxDate={tempEndDate || new Date()}
                  />
                </Box>
                
                <Box sx={{ minWidth: { xs: '100%', sm: 140 } }}>
                  <DatePicker
                    label={t('dashboard.endDate')}
                    value={tempEndDate}
                    format="dd/MM/yyyy"
                    onChange={(newValue) => setTempEndDate(newValue)}
                    textField={(params) => (
                      <TextField 
                        {...params} 
                        size="small"
                        fullWidth
                        sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(8px)',
                              borderRadius: 2,
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                              border: '1px solid rgba(148, 163, 184, 0.15)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                                borderColor: 'rgba(148, 163, 184, 0.25)',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                                borderColor: 'primary.main',
                                boxShadow: '0 0 0 2px rgba(52, 152, 219, 0.1), 0 4px 12px rgba(0, 0, 0, 0.08)',
                              }
                            },
                        }}
                      />
                    )}
                    minDate={tempStartDate}
                    maxDate={new Date()}
                  />
                </Box>
                
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  disabled={isLoading || !tempStartDate || !tempEndDate}
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: 2.5,
                    boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4), 0 4px 12px rgba(29, 78, 216, 0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(59, 130, 246, 0.5), 0 6px 16px rgba(29, 78, 216, 0.4), inset 0 1px 0 rgba(255,255,255,0.3)',
                    },
                    '&.Mui-disabled': {
                      background: 'rgba(148, 163, 184, 0.2)',
                      color: 'rgba(148, 163, 184, 0.6)',
                      boxShadow: 'none',
                    }
                  }}
                >
                  {isLoading ? t('dashboard.loading') : t('dashboard.search')}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </LocalizationProvider>

      {/* Businesses Analytics Sections */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 3 }}>
        {sortedBusinesses.map((business, businessIndex) => {
          const platformData = processBusinessAnalytics(business.business_id);
          const totalForBusiness = getTotalForBusiness(business.business_id);
          const businessLoading =
            isLoading &&
            !PINNED_BUSINESS_IDS.has(Number(business.business_id)) &&
            !businessAnalytics[business.business_id];
          const displayName =
            DISPLAY_NAME_BY_BUSINESS_ID[Number(business.business_id)] || business.brand_name;
          
          return (
            <Fade in timeout={300 + businessIndex * 100} key={business.business_id}>
              <Card sx={{ 
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                overflow: 'visible',
              }}>
                <CardContent sx={{ p: 3 }}>
                  {/* Business Header */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 800, 
                      color: '#0f172a', 
                      mb: 0.5,
                      fontSize: { xs: '1.3rem', sm: '1.5rem' },
                    }}>
                      {displayName}
                    </Typography>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Platform Cards */}
                  {businessLoading ? (
                    <Grid container spacing={2}>
                      {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
                          <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      {platformData.map((platform, index) => {
                        const PlatformIcon = platform.icon;
                        const percentage = getPlatformPercentage(platform.value, totalForBusiness);
                        
                        return (
                          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
                            <Card
                              sx={{
                                height: '100%',
                                background: platform.gradient,
                                color: 'white',
                                position: 'relative',
                                overflow: 'visible',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                borderRadius: 2,
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                                },
                              }}
                            >
                              <CardContent sx={{ 
                                p: 2.5, 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                justifyContent: 'space-between',
                              }}>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                  <Box sx={{ flex: 1 }}>
                                    <Typography 
                                      variant="h4" 
                                      sx={{ 
                                        fontWeight: 900, 
                                        mb: 1, 
                                        fontSize: { xs: '1.5rem', sm: '1.8rem' },
                                        textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                        color: 'white',
                                        lineHeight: 1.1,
                                      }}
                                    >
                                      {Math.round(platform.value).toLocaleString()}
                                    </Typography>
                                    <Typography 
                                      variant="h6" 
                                      sx={{ 
                                        fontWeight: 700,
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        textShadow: '0 1px 4px rgba(0,0,0,0.3)',
                                        color: 'rgba(255,255,255,0.98)',
                                      }}
                                    >
                                      {platform.title}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      width: 48,
                                      height: 48,
                                      borderRadius: '50%',
                                      background: 'rgba(255,255,255,0.2)',
                                      backdropFilter: 'blur(10px)',
                                      border: '2px solid rgba(255,255,255,0.3)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0,
                                      ml: 1,
                                    }}
                                  >
                                    {platform.icon === TikTokIcon ? (
                                      <PlatformIcon />
                                    ) : platform.icon === XHSIcon ? (
                                      <PlatformIcon />
                                    ) : (
                                      <PlatformIcon sx={{ fontSize: 24, color: 'white' }} />
                                    )}
                                  </Box>
                                </Box>
                                
                                <Box sx={{ mt: 2 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={percentage}
                                    sx={{
                                      height: 6,
                                      borderRadius: 3,
                                      backgroundColor: 'rgba(255,255,255,0.2)',
                                      mb: 1,
                                      '& .MuiLinearProgress-bar': {
                                        background: 'rgba(255,255,255,0.9)',
                                        borderRadius: 3,
                                      },
                                    }}
                                  />
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      color: 'rgba(255,255,255,0.95)', 
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                                    }}
                                  >
                                    {percentage.toFixed(1)}% of brand total
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Fade>
          );
        })}
      </Box>

      {businesses.length === 0 && !loading && (
        <Card sx={{ 
          p: 6, 
          textAlign: 'center',
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 2
          }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1
            }}>
              <AnalyticsIcon sx={{ 
                fontSize: 40, 
                color: '#1976d2',
                opacity: 0.7
              }} />
            </Box>
            <Typography variant="h5" sx={{ 
              fontWeight: 600, 
              color: '#374151',
              mb: 0.5
            }}>
              {t('masterDashboard.noAnalytics', { defaultValue: 'No analytics are available' })}
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#6b7280',
              maxWidth: 400
            }}>
            </Typography>
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default MasterDashboard;
